import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isBrowser, isMobile, isMobileOnly } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import ReactTable, { Column, DerivedDataObject, Instance, Resize } from 'react-table-6';
import { HasClassName } from '../component-utils';
import { useSelector } from '../hook-utils';
import { removeEmbededExtensionDetails, removeEmbededExtensionProperties, revertEmbededExtensionProperties, saveEmbededExtensionProperties, showEmbededExtensionProperties, showEmbedExtensionDetails } from '../legacy';
import { Extension, getPresenceText, Identifier, Region, sameIdentifier } from '../model';
import { actions } from '../store';
import { selectDateFormating, selectFavExtensionFolderId, selectIsExpertise, selectMyAccess, selectMyself, selectPresenceMap, selectRegions, selectShowMainToolbar } from '../store/reducers/config';
import { getGridSortOrder, selectDirectoryView, selectGridColumnWidths, ColumnWidths } from '../store/reducers/localSettings';
import { getSelectedExtension, selectExtension, selectRegionExtenions } from '../store/reducers/queueAgents';
import { TableRow } from '../table-utils';
import { useTeams } from '../TeamsProvider';
import ExtensionMenu from './AgentMenu';
import { formatReturnDateTime, PresenceInfo } from './AgentsTable';
import Button from './Button';
import './ExtensionsPage.scss';
import { ReactComponent as SearchIcon } from './icons/toolbar-search.svg';
import Input from './Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from './Menu';
import MobileExtensionsTable from './MobileView/ExtensionPage';
import MoreButton from './MoreButton';
import ProfilePhoto from './ProfilePhoto';

interface MenuState {
  target: MenuTarget
  item: Extension
}

export default function ExtensionsPage() {
  console.log('Render ExtensionsPage')

  const dispatch = useDispatch()
  const view = useSelector(selectDirectoryView)
  const mySelf = useSelector(selectMyself)
  const regions = useSelector(selectRegions)
  const folderId = useSelector(selectFavExtensionFolderId)
  const [filter, setFilter] = useState('')
  const [filterKeyPress, setFilterKeyPress] = useState('')

  const region = regions && regions.find(x => x.id === view.regionId)
  const name = folderId === view.regionId ? 'Favourites' : region ? region.name : undefined
  useEffect((() => {
    if (!name) return
  }), [dispatch, regions, folderId, name])


  if (!mySelf || !name) return null

  const table = isBrowser ? <ExtensionsTable regionId={view.regionId} filter={filterKeyPress} /> : <MobileExtensionsTable regionId={view.regionId} filter={filter} />

  return <div className={classNames('ExtensionTablePage', isMobile && 'ExtensionMobileTablePage')}>
    <div className='Header'>
      <ExtensionsPageHeader name={name} />
      <div className='searchBox'>
        <Input placeholder='Name / department / extension' className='search' value={filter}
          onChange={e => setFilter(e.currentTarget.value)}
          autocomplete={false}
          onKeyPress={e => {
            //key press is needed for enter to stop form submitting
            if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
              e.stopPropagation()
              e.preventDefault()

              setFilterKeyPress(filter)
              dispatch(actions.updateSelectedExtension(undefined))
            }
          }}
          onKeyUp={e => {
            // escape only works for keyup
            if (e.nativeEvent.code === 'Escape') {
              setFilter('')
              setFilterKeyPress('')
            }

          }} />
        <SearchIcon className='searchIcon' />
      </div>
    </div>
    {table}
  </div>
}

function ExtensionsPageHeader({ name }: { name: string }) {
  const { inTeams } = useTeams()
  return <h1 className={classNames('GridPageHeader', !inTeams && 'standalone', isMobileOnly && 'GridPageHeaderMobile')}>
    {name}
  </h1>
}

interface Props extends HasClassName {
  regionId: string
  filter: string
}

export function ExtensionsTable({ className, regionId, filter }: Props) {
  const [menuState, setMenuState] = useState<MenuState>()
  const dispatch = useDispatch()
  const extensions = useSelector(s => selectRegionExtenions(s, regionId))
  const folderId = useSelector(selectFavExtensionFolderId)
  const data = useMemo(() => extensions ? extensions : [], [extensions])
  const regions = useSelector(selectRegions)
  const myId = useSelector(selectMyself)
  const isExpertise = useSelector(selectIsExpertise)
  const selected = useSelector(getSelectedExtension)
  const access = useSelector(selectMyAccess)
  const reactTable = useRef<ReactTable<Extension>>(null);
  const lastFilter = useRef<string>('');

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  const gridType = 'extensions'
  const sorted = useSelector(s => getGridSortOrder(s, gridType))
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()

  const onMore = useCallback((e: React.SyntheticEvent, item: Extension, target: MenuTarget) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target, item })
  }, [])

  useEffect((() => {
    if (!extensions && folderId) {
      dispatch(actions.fetchExtensionsRequeust(regionId, folderId))
    } else {
      return
    }
  }), [dispatch, regionId, extensions, folderId])

  useEffect(() => {
    return () => {
      //on unload
      dispatch(actions.setSelectedExtension(undefined))
      dispatch(actions.clearSelectedNumber())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onKeyDown(e: KeyboardEvent) {
    if (e.target instanceof HTMLElement) {
      const element = e.target as HTMLElement
      if (element.id.indexOf("mce") === 0) {
        return
      }
    }
    if (data == null || data.length === 0 || reactTable.current === null) return

    const table = reactTable.current as Instance
    const sorted: Extension[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)

    function selectFirstRow() {
      if (!selected && sorted.length > -1) {
        const first = sorted[0]
        dispatch(actions.setSelectedNumber(first.id))
        dispatch(actions.setSelectedExtension(first))
        return true
      }
      return false
    }

    function selectNextUp() {
      const index = sorted.findIndex(d => selected !== null && selected.id.id === d.id)
      return index - 1
    }

    function selectNextDown() {
      const index = sorted.findIndex(d => selected !== null && selected.id.id === d.id)
      return index + 1
    }

    switch (e.keyCode) {
      case 38://up
        e.preventDefault()

        if (selectFirstRow()) return

        const prevIndex = selectNextUp()
        if (prevIndex < 0) return
        const prev = sorted[prevIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //

        } else {
          dispatch(actions.setSelectedNumber(prev.id))
          dispatch(actions.setSelectedExtension(prev))
        }
        break
      case 40://down
        e.preventDefault()

        if (selectFirstRow()) return

        const nextIndex = selectNextDown()
        if (nextIndex >= sorted.length) return
        const next = sorted[nextIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //
        } else {
          dispatch(actions.setSelectedNumber(next.id))
          dispatch(actions.setSelectedExtension(next))
        }

        break
    }
  }

  const onKeyDownRef = useRef(onKeyDown)

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  })

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      onKeyDownRef.current(e)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const f = filter.toLowerCase()
  if (lastFilter.current !== f) {
    lastFilter.current = f
    setPage(0)
  }

  const region = f !== '' && regions ? regions.filter(r => r.name.toLowerCase().indexOf(f) > -1) : undefined
  const filtered = useMemo(() => {
    return f !== '' ? data.filter(c => {
      return c.id.toLowerCase().indexOf(f) > -1 ||
        c.name.toLowerCase().indexOf(f) > -1 ||
        c.department.toLowerCase().indexOf(f) > -1 ||
        c.expertise.toLowerCase().indexOf(f) > -1 ||
        c.server.toLowerCase().indexOf(f) > -1 || (region && region.find(r => r.id === c.regionId) !== undefined)
    }) : data
  }, [data, f, region])

  // switch to the right page to show the selected extension
  useEffect(() => {
    if (!selected) { return }
    const index = filtered.findIndex(x => sameIdentifier(x, selected.id))
    if (index !== -1) {
      setPage(Math.floor(index / pageSize))
    }
  }, [filtered, pageSize, selected])

  const onColumnResize = useCallback((newResized: Resize[], event: any) => {
    newResized.forEach(element => {
      if (refColumnResizeState.current) {
        refColumnResizeState.current = { ...refColumnResizeState.current, [element.id]: element.value }
      } else {
        refColumnResizeState.current = { [element.id]: element.value }
      }
    });
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  if (!regions || !access || !myId) return null

  const columns = getColumns(onMore, regions, widths).filter(c => isExpertise ? true : c.id !== 'expertise')
  return <div className='ExtensionTableBody'>
    <ReactTable className={classNames('ExtensionTable', className)} ref={reactTable}
      data={filtered}
      columns={columns}
      showPagination={true}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      minRows={0}
      loading={!extensions}
      sorted={sorted}
      NoDataComponent={() => null}
      getTrProps={(_: any, row?: TableRow<Extension>): React.HtmlHTMLAttributes<HTMLTableRowElement> | undefined => row && ({
        className: ((selected && row.original.id === selected.id.id && row.original.server === selected.id.server) || (menuState && row.original.id === menuState.item.id && row.original.server === menuState.item.server)) ? 'selected' : undefined,
        onContextMenu(e) {
          setMenuState({ target: targetFromEvent(e), item: row.original })
        },
        onClick() {
          dispatch(actions.setSelectedNumber(row.original.id))
          dispatch(actions.setSelectedExtension(row.original))
        },
      })}
      onSortedChange={(newSort, column) => {
        dispatch(actions.requestSetGridSortOrder(gridType, [newSort[0]]))
      }}
      onResizedChange={onColumnResize}
    />
    {menuState && <ExtensionMenu {...menuState}
      extension={menuState.item}
      onClose={() => setMenuState(undefined)} />}

    {selected && ((!selected.edit && (myId.id === selected.id.id || access.features.clientViewExtensionDetails)) || (selected.edit && ((myId.id === selected.id.id && access.features.configMyExtension) || access.features.configAllExtensions))) && <RenderSelection id={selected.id} edit={selected.edit} myId={myId} />}

  </div >
}

export function RenderSelection({ id, myId, edit }: { id: Identifier, myId: Identifier, edit?: boolean }) {
  const access = useSelector(selectMyAccess)
  const showMainToolbar = useSelector(selectShowMainToolbar)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [dirty, setDirty] = useState(false)
  const extension = useSelector(s => selectExtension(s, id))
  const divId = useRef<string>('');
  const dispatch = useDispatch()
  const cb = useCallback((div: HTMLDivElement | null) => {
    if (div === null && divId.current !== '') {
      console.info("Removing embeded items " + divId.current)
      // div is being removed by react, do cleanup here
      if (edit === true) {
        removeEmbededExtensionProperties(divId.current)
      } else {
        removeEmbededExtensionDetails(divId.current)
      }
    } else {
      // put stuff into div
      if (edit === true) {
        divId.current = showEmbededExtensionProperties(div, id, (x) => {
          setDirty(x === '1');
        }, () => {
          //forced close by ui
          dispatch(actions.setSelectedExtension(id, false))
        })
      } else {
        divId.current = showEmbedExtensionDetails(div, id, myId)
      }

      console.info("Added embeded items " + divId.current)
    }

  }, [dispatch, edit, id, myId])


  //should always been there so don't try and load it
  if (!extension) return null

  const canEdit = (myId.id === id.id && access.features.configMyExtension) || access.features.configAllExtensions
  const canView = access.features.clientViewExtensionDetails
  ////SendRecordGreetingCommand("contact-personal", 'record'|'delete', id_ + "-Extension", servername_, extension_, null);
  return <div className='ExtensionPage'>
    <h2>
      {id.id} - {extension.name}
      <Button primary disabled={!edit || (edit === true && !dirty)} onClick={() => saveEmbededExtensionProperties()}>Save</Button>
      {showMainToolbar && <Button innerRef={menuButtonRef} onClick={() => setShowMenu(true)}>...</Button>}
      {showMainToolbar && <Button onClick={() => dispatch(actions.updateSelectedExtension(undefined))}>X</Button>}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} target={menuButtonRef.current} placement='bottom-end'>
        <MenuItem onClick={() => dispatch(actions.updateSelectedExtension(undefined))}>Cancel</MenuItem>
        <MenuItem disabled={!edit || (edit === true && !dirty)} onClick={() => {
          revertEmbededExtensionProperties()
          setDirty(false)
        }}>Revert</MenuItem>
        {((canView && edit === true) || (canEdit && edit !== true)) && <MenuDivider />}
        {canView && edit === true && <MenuItem onClick={() => dispatch(actions.setSelectedExtension(id, false))}>Details</MenuItem>}
        {canEdit && edit !== true && <MenuItem onClick={() => dispatch(actions.setSelectedExtension(id, true))}>Edit</MenuItem>}
        {canEdit && !access.isLyncLicensed && <MenuDivider />}
        {canEdit && !access.isLyncLicensed && <MenuItem onClick={() => dispatch(actions.recordGreeting('contact-personal', 'record', id.id + '-Extension', id.server))}>Record Greeting</MenuItem>}
        {canEdit && !access.isLyncLicensed && <MenuItem onClick={() => dispatch(actions.recordGreeting('contact-personal', 'delete', id.id + '-Extension', id.server))}>Delete Greeting</MenuItem>}
      </Menu>
    </h2>
    <div ref={cb} />
  </div>
}

function getColumns(onMore: (e: React.MouseEvent, item: Extension, target: MenuTarget) => void, regions: Region[], columnsWidths?: ColumnWidths): Array<Column<Extension>> {

  const cols: Column<Extension>[] = [{
    id: 'presence',
    accessor: a => a.presence,
    className: 'presence',
    Cell: ({ original: a }: TableRow<Extension>) => <PresenceInfo extension={a} />,
    width: 42,
    resizable: false,
  }, {
    id: 'extension',
    Header: 'Extension',
    accessor: a => a.id,
    width: 75,
  }, {
    id: 'details',
    Header: 'Details',
    accessor: a => a.name,
    Cell: ExtensionCell,
    width: 260,
  }, {
    id: 'department',
    Header: 'Department',
    accessor: a => a.department,
    Cell: (row: TableRow<Extension>) => row.original.department ? <div title={row.original.department}>{row.original.department}</div> : null,
  }, {
    id: 'expertise',
    Header: 'Expertise',
    accessor: a => a.expertise,
    Cell: (row: TableRow<Extension>) => row.original.expertise ? <div title={row.original.expertise}>{row.original.expertise}</div> : null,
  }, {
    id: 'region',
    Header: 'Region',
    Cell: (row: TableRow<Extension>) => {
      const region = regions.find(r => r.id === row.original.regionId)
      return region ? <div title={region.name}>{region.name}</div> : null
    },
  }, {
    id: 'server',
    Header: 'Server',
    accessor: a => a.server,
  }, {
    id: 'actions',
    accessor: q => <MoreButton className='menu-button' onClick={e => onMore(e, q, e.currentTarget)} />,
    width: 35,
    sortable: false,
    resizable: false,
  },
  ]

  return cols.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

export function ExtensionInfo({ extension }: { extension: Extension }) {
  const format = useSelector(selectDateFormating)
  const presenceMap = useSelector(selectPresenceMap)

  if (format === null) return null

  return <>
    <ProfilePhoto className='photo' extension={extension} name={extension.name} datetime={extension.imageImageDate} />
    <div className='agentDetails'>
      <div>{extension.name}</div>
      <div className='presence'>
        {getPresenceText(presenceMap, extension)}
        {extension.returnTime && <span title={dayjs(extension.returnTime).format('LL LT')}>{' until ' + formatReturnDateTime(extension.returnTime, format.longDateFormat, format.shortTimeFormat)}</span>}
      </div>
    </div>
  </>
}

export function ExtensionCell(row: TableRow<Extension>) {
  return <div className='agent'>
    <ExtensionInfo extension={row.original} />
  </div>
}
